/* eslint-disable ts/ban-ts-comment */
/* eslint-disable no-console */
/* eslint-disable unused-imports/no-unused-vars */
import { createHttpClient, type IRequestProxy } from './request'

interface LogContext {
    message: string
    level: string
    page: string
    host: string
    path: string
    queryString: string
    scheme: string
}

export interface IClientLogProxy {
    sendLogAsync: (logContext: LogContext) => Promise<void>
}

interface ErrorContext {
    message: string
    exceptionStack?: string
    exceptionMessage?: string
    exceptionType?: string
}

export interface ILogger {
    logError: (message: string, error?: unknown) => void

    logInformation: (message: string) => void

    logDebug: (message: string) => void
}

type LogLevels = 'error' | 'information' | 'debug'

export class ConsoleLogger implements ILogger {
    logError(message: string, error?: unknown): void {
        console.error(message, error)
    }

    logInformation(message: string): void {
        console.log(message)
    }

    logDebug(message: string): void {
        console.debug(message)
    }
}

export class EmptyLogger implements ILogger {
    logError(message: string, error?: unknown): void {}

    logInformation(message: string): void {}

    logDebug(message: string): void {}
}

type ApiLoggerOptions = Pick<LoggerOptions, 'allowLevels' | 'pageName'>

export class ApiLoggerDecorator implements ILogger {
    private _options: Required<ApiLoggerOptions>

    constructor(private _logger: ILogger, private _clientLogProxy: IClientLogProxy, options: ApiLoggerOptions) {
        this._options = {
            pageName: options.pageName,
            allowLevels: options.allowLevels ?? ['error', 'information', 'debug'],
        }
    }

    logError(message: string, error?: unknown) {
        this._logger.logError(message, error)
        const errorContext: ErrorContext = {
            message,
            ...this.getExceptionProperties(error),
            ...this.getOtherProperties(error),
        }

        this.sendLogRequest('error', JSON.stringify(errorContext))
    }

    logInformation(message: string) {
        this._logger.logInformation(message)
        this.sendLogRequest('information', message)
    }

    logDebug(message: string) {
        this._logger.logDebug(message)
        this.sendLogRequest('debug', message)
    }

    private getOtherProperties(error: unknown): Record<string, any> {
        if (error && typeof error === 'object') {
            const IGNORE_KEYS = ['stack', 'message', 'name', 'tempExceptionStack']
            let propertyFound = false
            const properties: Record<string, any> = {}
            Object.keys(error).forEach((key) => {
                if (IGNORE_KEYS.includes(key)) return

                let val = (error as Record<string, any>)[key]
                if (val === null || typeof val === 'function') return

                if (typeof val === 'object' && typeof val.toISOString === 'function') {
                    val = val.toISOString()
                }

                properties[key] = val
                propertyFound = true
            })

            if (propertyFound) {
                return properties
            }
        }
        return {}
    }

    private getExceptionProperties(error: unknown) {
        if (error && error instanceof Error) {
            let exceptionStack = ''
            // @ts-expect-error
            const { message: exceptionMessage, stack, name: exceptionType, tempExceptionStack } = error

            if (tempExceptionStack) {
                exceptionStack = `${exceptionType}: ${exceptionMessage} ${tempExceptionStack}`
            }
            else {
                exceptionStack = stack ?? ''
            }

            return { exceptionStack }
        }
        return {}
    }

    private sendLogRequest(level: LogLevels, message: string) {
        if (!this._options.allowLevels.includes(level)) return

        this._clientLogProxy
            .sendLogAsync({
                level,
                message,
                scheme: location.protocol.slice(0, -1),
                host: location.host,
                path: location.pathname,
                queryString: location.search,
                page: this._options.pageName,
            })
            .then()
            .catch(e => console.error(e))
    }
}

class LogApiProxy implements IClientLogProxy {
    constructor(private httpClient: IRequestProxy) {}

    async sendLogAsync(logContext: LogContext): Promise<void> {
        return this.httpClient.post({
            url: '/api/track/ClientLog',
            data: logContext,
        })
    }
}

export interface LoggerOptions {
    pageName: string
    allowLevels?: LogLevels[]
    enableConsole: boolean
    enableApi: boolean
    refererDomain: string
}

export class LoggerFactory {
    public static createLogger(options: LoggerOptions): ILogger {
        const logger = options.enableConsole ? new ConsoleLogger() : new EmptyLogger()
        return !options.enableApi
            ? logger
            : new ApiLoggerDecorator(logger, this.createLogApiProxy(options.refererDomain), options)
    }

    private static createLogApiProxy(refererDomain: string) {
        return new LogApiProxy(
            createHttpClient({
                baseURL: `${location.protocol}//cmgw.${refererDomain}`,
            }),
        )
    }
}
